import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, fmtCur, CHART_COLORS } from "@/lib/api";
import { Users, User, Building2, UserPlus, ShieldCheck, Clock, GitBranch, DollarSign, TrendingUp, ArrowRight } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";

const KPIS = [
  { key: "total_clients", label: "Total Clients", icon: Users, fmt: fmtNum },
  { key: "individuals", label: "Individuals", icon: User, fmt: fmtNum },
  { key: "companies", label: "Corporate", icon: Building2, fmt: fmtNum },
  { key: "new_this_week", label: "New This Week", icon: UserPlus, fmt: fmtNum },
  { key: "verified", label: "Verified (KYC)", icon: ShieldCheck, fmt: fmtNum },
  { key: "pending", label: "Pending KYC", icon: Clock, fmt: fmtNum },
  { key: "total_referrals", label: "Total Referrals", icon: GitBranch, fmt: fmtNum },
  { key: "avg_clients_per_referral", label: "Avg Clients / Referral", icon: TrendingUp, fmt: (n) => n },
  { key: "total_deposits", label: "Total Deposits", icon: DollarSign, fmt: fmtCur },
];

export default function Overview() {
  const navigate = useNavigate();
  const { data: stats, isLoading } = useQuery({ queryKey: ["overview"], queryFn: () => api.get("/stats/overview").then((r) => r.data) });
  const { data: growth } = useQuery({ queryKey: ["growth"], queryFn: () => api.get("/stats/growth").then((r) => r.data) });
  const { data: refs } = useQuery({ queryKey: ["referrals-top"], queryFn: () => api.get("/referrals").then((r) => r.data) });

  const empty = stats && stats.total_clients === 0;

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Executive Overview</h1>
        <p className="mt-1 text-sm text-muted-foreground">Live snapshot of your referral network and client base.</p>
      </div>

      {empty && (
        <div className="rounded-md border border-dashed border-border bg-card p-8 text-center" data-testid="overview-empty">
          <p className="text-sm text-muted-foreground">No data yet. Import your first CSV to see statistics.</p>
          <button
            onClick={() => navigate("/import")}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            data-testid="overview-import-cta"
          >
            Import CSV <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {KPIS.map((k, i) => (
          <div
            key={k.key}
            className="grad-top grad-hover rounded-md border border-border bg-card p-5 transition-colors duration-200 animate-fade-up"
            style={{ animationDelay: `${i * 40}ms` }}
            data-testid={`kpi-${k.key}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{k.label}</span>
              <k.icon className="h-4 w-4 text-[#2b9bb7]" />
            </div>
            <div className="mt-3 font-heading text-3xl font-semibold tabular text-foreground">
              {isLoading ? <span className="inline-block h-8 w-16 animate-pulse rounded bg-[#E6EFF2]" /> : k.fmt(stats?.[k.key] ?? 0)}
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-md border border-border bg-card p-5 lg:col-span-2">
          <h3 className="mb-4 font-heading text-lg font-medium">New Clients (last 90 days)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={growth?.by_day || []}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2b9bb7" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2b9bb7" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#575E5A" }} tickLine={false} axisLine={false} minTickGap={40} />
              <YAxis tick={{ fontSize: 11, fill: "#575E5A" }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e5e2dc", fontSize: 12 }} />
              <Area type="monotone" dataKey="clients" stroke="#2b9bb7" strokeWidth={2} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-md border border-border bg-card p-5">
          <h3 className="mb-4 font-heading text-lg font-medium">By KYC Status</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={growth?.by_kyc || []} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {(growth?.by_kyc || []).map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e5e2dc", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1">
            {(growth?.by_kyc || []).slice(0, 5).map((s, i) => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  {s.name}
                </span>
                <span className="font-mono">{fmtNum(s.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top referrals */}
      <div className="rounded-md border border-border bg-card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-medium">Top Referrals</h3>
          <button onClick={() => navigate("/referrals")} className="flex items-center gap-1 text-sm text-primary hover:underline" data-testid="view-all-referrals">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="space-y-2">
          {(refs || []).slice(0, 5).map((r) => (
            <div
              key={r.referral}
              onClick={() => navigate(`/referrals/${encodeURIComponent(r.referral)}`)}
              className="flex cursor-pointer items-center justify-between rounded-md px-3 py-2.5 transition-colors hover:bg-[#E6EFF2]"
              data-testid={`top-referral-${r.referral}`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">#{r.rank}</span>
                <div className="min-w-0">
                  {r.name && <div className="truncate text-sm font-medium">{r.name}</div>}
                  <div className="truncate font-mono text-xs text-muted-foreground">{r.referral}</div>
                </div>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <span className="tabular">{fmtNum(r.clients)} clients</span>
                <span className="tabular text-muted-foreground">{fmtCur(r.deposited)}</span>
              </div>
            </div>
          ))}
          {refs && refs.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">No referrals yet.</p>}
        </div>
      </div>
    </div>
  );
}
